import { Injectable } from '@angular/core';
import { Http, Headers, RequestOptions, Response, URLSearchParams } from '@angular/http';
import { Observable } from 'rxjs';
import 'rxjs/Rx';
import { pipe } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomQueryEncoderHelper } from '../common/custom.query.encoder';

import { Environment } from '../app.environment';
import {
    Allergen,
    AllergenData,
    Authorization,
    ChartFile,
    ExamData,
    ExamSummary,
    EmailExists,
    EmailExistsResult,
    ForgotPasswordResult,
    Intake,
    IntakeAllergen,
    IntakeMedication,
    MedicalCondition,
    PasswordReset,
    Patient,
    PatientActivationInfo,
    PatientAdditional,
    PatientAllergen,
    PatientAvatarResult,
    PatientPlan,
    PatientPreferedPharmacy,
    Payment,
    Profile,
    ProviderBio,
    PharmacyInfo,
    RetrySMS,
    ScheduleResponse,
    State,
    StateCodeCheck,
    Surgery,
    SurgicalList,
    SurveyModel,
    Timezone,
    Token,
    TwilioSMS,
    User,
    VisitSchedule,
    WaitingRoom,
    MediaPermisionData,
    PatientPlanTypes,
    ProviderScheduleSearch,
    AvailableProviderQueue,
    ProviderScheduleForPatient,
    PatientScheduleIntakeData,
    PatientScheduledVisits,
    PatientMedicalCondition,
    ProviderBioFullDetail,
    LabworkRequestModel,
    RequestVerification,
    ConditionDisplay
} from '../models/index';
import { Payment as SFPayment, PaymentMethod, PaymentMethodResult, IntakeTransactionResponse } from '../models/sf-payments'
import { AwsEndpoints } from './api.aws.request';

export class Endpoint {
    constructor(relativeUrl: string) { this.relativeUrl = relativeUrl };
    relativeUrl: string;
    getUrl(paramDict = {}) {
        let result = this.relativeUrl;
        for (let key in paramDict) {
            result = result.replace('{' + key + '}', paramDict[key]);
        }
        return result;
    }
}

export class Endpoints {

    static getPatient = new Endpoint('v2/patient/{userId}');
    static getPatientById = new Endpoint('v2/admin/patient/{patientId}');
    static getRegistrationStatus = new Endpoint('v2/patient/getactivationinfo');
    static updatePatient = new Endpoint('v2/admin/patient/{patientId}');
    static updatePatientProfile = new Endpoint('v2/patient/{patientId}');
    static createPatient = new Endpoint('v2/patient/');
    static createPatientAvatar = new Endpoint('v2/patient/{userId}/avatar');
    static createIntake = new Endpoint('v2/intake/begin');
    static getPreferredPharmacy = new Endpoint('v2/pharmacy/search/memberpreferred/all/{userId}');
    static getPatientHistoryAdditional = new Endpoint('v2/patient/{userId}/HistoryAdditional');
    static upsertPatientSurvey = new Endpoint('v2/survey');
    static updatesmokingalcohol = new Endpoint('v2/patient/{userId}/smokingalcohol');
    static setPassword = new Endpoint('v2/patient/setpassword');

    //For BH check the policy type
    static getUserPolicyType = new Endpoint('v2/patient/{userId}/policyType');
    static getAssessmentsAssignedToPatient = new Endpoint('v3/assessments/{patientId}');

    static getBHScreenSSO = new Endpoint('v2/user/{intakeId}/authenticate');

    //Intake
    static getActiveIntake = new Endpoint('v2/intake/active/{userId}');
    static getIntake = new Endpoint('v2/intake/{intakeId}');

    static getPatientPoliciesByLineOfBusiness = new Endpoint('v2/patient/{userId}/paymentPlans/{lineOfBusiness}');
    static capturePayment = new Endpoint('v2/intake/{intakeId}/capturePayment');
    static completeIntake = new Endpoint('v3/intake/{intakeId}/complete/{planId}');
    //Check if the visit is a follow up to get the correct pricing. 
    static checkPhycFollowUpStatus = new Endpoint('v2/patient/{patientId}/provider/{providerId}/intake/{intakeId}/psyfollowupallowed');

    //Dependents
    static getDependents = new Endpoint('v2/patient/{userId}/dependent');
    static createDependent = new Endpoint('v2/patient/{userId}/dependent');
    static createDependentES = new Endpoint('v2/patient/{userId}/dependent/ES');
    static updateDependent = new Endpoint('v2/patient/{userId}/dependent');

    //Pharmacy
    static searchPharmacy = new Endpoint('v2/pharmacy/search?maxDistance={distance}&skip=0&take={nPage}');
    static updateIntakePharmacy = new Endpoint('v2/intake/{intakeId}/pharmacy');

    //Provider
    static getProviderBios = new Endpoint("v2/provider/{lineOfBusiness}/bio/{stateCode}");
    static getProviderBiosByType = new Endpoint("v2/provider/{lineOfBusiness}/bio/{stateCode}/type/{bhType}");
    static getProviderBioDetail = new Endpoint("v2/provider/{providerId}/bio");

    //User
    static emailExists = new Endpoint('v2/user/emailexists');
    static getUserProfile = new Endpoint('v2/user/{userId}/profile');
    static getExternalUserId = new Endpoint('v2/user/{userId}/externalid');
    static forgotPassword = new Endpoint('v2/user/forgotPassword');
    static forgotPasswordES = new Endpoint('v2/user/forgotPassword/ES');
    static token = new Endpoint('v2/user/login');

    //this is technically account related
    static getDependentsAllowed = new Endpoint('v2/user/{userId}/dependents/allowed');

    //Intake Docs
    static getIntakeFile = new Endpoint("v2/admin/intake/{intakeId}/files");
    static uploadIntakeFiles = new Endpoint('v2/intake/{intakeId}/files');
    static removeIntakeFile = new Endpoint('v2/admin/intake/{intakeId}/file/{fileId}/delete');

    //Intake Docs
    static getIntakePDF = new Endpoint('v2/intake/{intakeId}/pdf');
    static getExcuseForm = new Endpoint('v2/provider/intake/{intakeId}/excuseForm/pdf');
    static getIntakeMedications = new Endpoint('v2/intake/medications/{intakeId}');
    static insertIntakeMedications = new Endpoint('v2/intake/medications/{intakeId}');

    // DEV-5544
    static getIntakeAllergens = new Endpoint('v2/intake/allergy/{intakeId}');
    static insertIntakeAllergens = new Endpoint('v2/intake/allergy/{intakeId}');

    //Visit History 
    static getExamSummary = new Endpoint('v2/history/list/{userId}');
    static getExamDetail = new Endpoint('v2/history/visit/{intakeId}');
    static getCareInstructionsPdfLinks = new Endpoint('v2/history/visit/{intakeId}/careinstructionlinks')

    //Schedule
    //Get scheduled visits for dashboard view 
    static getUpcomingVisits = new Endpoint("v2/intake/active/{patientId}/scheduled");
    static getBHVisitSchedule = new Endpoint('v2/admin/intake/{intakeId}/schedule');
    //Save requested Schedule for BH
    static updateScheduleRequestforIntake = new Endpoint("v2/intake/{intakeId}/schedule");
    static getProviderAvailabilityQueue = new Endpoint('v2/schedule/ActiveProviders');
    static getProviderScheduleForPatientChange = new Endpoint('v2/schedule/appointment/{scheduleId}/get');
    static saveScheduleForPatient = new Endpoint('v2/schedule/crud/{providerId}/lob/{lob}');
    static saveScheduleRequestWithProvider = new Endpoint('v2/admin/intake/{intakeId}/schedule');
    static saveScheduledIntakeForPatient = new Endpoint('v2/schedule/patientAppointment/save');
    static makeScheduleRequest = new Endpoint('v2/schedule/patientAppointment/request');
    static confirmcheduleRequest = new Endpoint('v2/schedule/patientAppointment/confirm/{requestId}');
    //patientAppointment/confirm/{requestId}
    //v2/schedule/patientAppointment/request
    //Calendar
    static getStates = new Endpoint('v2/lookup/state/list');
    static getTimezones = new Endpoint('v2/lookup/timezones/producer');
    static checkStateIsValidForService = new Endpoint('v2/lookup/state/check');

    //Medical Conditions
    static updateMedicalConditions = new Endpoint('v2/patient/{userId}/Conditions');
    static insertMedicalConditions = new Endpoint('v3/medicalHistory/{userId}/Condition');
    static deletePatientCondition = new Endpoint('v3/medicalHistory/{userId}/condition/{conditionId}');
    static getMedicalConditionsByServiceLine = new Endpoint('v2/lookup/Conditions/Admin/{lob}');
    static getMedicalConditions = new Endpoint('v2/lookup/Condition/{description}');
    static getMedicalConditionsByLob = new Endpoint('v2/lookup/Conditions?search={description}&lob={lob}');
    static getPatientSurgeries = new Endpoint('v2/patient/{userId}/Surgeries');
    static updatePatientSurgeries = new Endpoint('v2/patient/{userId}/Surgeries');
    static insertPatientSurgery = new Endpoint('v2/patient/{userId}/Surgeries');
    static deletePatientSurgery = new Endpoint('v2/patient/{userId}/Surgeries/{surgeryId}');
    static searchSurgeries = new Endpoint('v2/lookup/Surgeries?search={description}');
    static getSurgicalList = new Endpoint('v2/lookup/SurgicalHistoryItems');
    static getPatientAllergenList = new Endpoint('v2/patient/{userId}/allergy');
    static updatePatientAllergy = new Endpoint('v2/patient/{userId}/allergy');
    static deletePatientAllergy = new Endpoint('v2/patient/{userId}/allergy/{allergyId}');
    static getAllergens = new Endpoint('v2/allergy/allergen?description={description}');
    static searchMedication = new Endpoint('v2/medication/search?name={searchName}');

    static getPatientPlans = new Endpoint('v2/admin/patient/{patientId}/plans/{lineOfBusiness}');
    static getAllPatientPlans = new Endpoint('v2/admin/patient/{patientId}/plans');
    static getPatientPlanTypes = new Endpoint('v2/patient/{userId}/productTypes');


    static saveMediaConfig = new Endpoint("v2/intake/videoCheck");

    //For saving payments and payment methods.
    static saveNewPaymentMethod = new Endpoint("v3/intake/PaymentMethod/{userId}");
    static getPaymentMethods = new Endpoint("v3/intake/PaymentMethod/{userId}");
    static updatePaymentMethod = new Endpoint("v3/intake/PaymentMethod/{cardId}");
    static processPayementforIntake = new Endpoint("v3/intake/PaymentMethod/{cardId}/charge");
    static schedulePayementforIntake = new Endpoint("v3/intake/PaymentMethod/{cardId}/schedule");    

    static updatePatienthasLabwork = new Endpoint("v2/admin/labwork/{intakeId}/status/LP");
    static getPatientLabwork = new Endpoint("v2/admin/labwork/{userId}");

    //Notify APIS
    static reserveScheduleRequest = new Endpoint("api/notify/createjob");
    static cancelReservedScheduleRequest = new Endpoint("api/notify/canceljob/{jobId}");
    static verifyReservedScheduleRequest = new Endpoint("v3/partner/schedule/request/{requestId}/verify");
   //static reserveScheduleRequest = new Endpoint("api/values");
}

export class HttpMethods {
    static get = 'GET';
    static post = 'POST';
    static put = 'PUT';
    static delete = 'DELETE';
}

@Injectable()
export class Api {

    public dataUser: any[];
    examList: any = {};
    _api: AwsEndpoints;

    constructor(private env: Environment, private http: Http, api: AwsEndpoints) { 
        this._api = api;
    }

    private convertToUrlSearchParams(obj: any) {
        let params = new URLSearchParams('', new CustomQueryEncoderHelper());
        for (let key in obj) {
            params.append(key,  obj[key].toString());
        }

        return params;
    }

    // api class does NOT set, nor manage it's own bearer token, this application layer was only meant to send and recieve requests
    static bearerToken: string;

    private request<T>(method: string, relativeUrl: string, searchParams?: any, requestBody?: any, requestHeaders?: Headers, useBearerToken = true): Observable<T> {
        let options = new RequestOptions();
        options.headers = requestHeaders || new Headers();
        options.method = method.toUpperCase();

        options.body = options.body || requestBody;
        if (searchParams) options.search = options.search ||  this.convertToUrlSearchParams(searchParams);

        options.headers.append('Accept', 'application/json');
        //options.headers.append('Content-Type', 'application/json');

        if (Api.bearerToken && useBearerToken) {
            options.headers.append('Authorization', 'Bearer ' + Api.bearerToken);
        }
        console.log('endpoint:', this.env.settings.apiUrl + relativeUrl);

        return this.http.request(this.env.settings.apiUrl + relativeUrl, options)
            .map((res: Response) => { return res.json(); })
            .map((stream => { return stream; }))
            .share();
    }



    private notify<T>(method: string, relativeUrl: string, searchParams?: any, requestBody?: any, requestHeaders?: Headers, useBearerToken = true): Observable<T> {
        let options = new RequestOptions();
        options.headers = requestHeaders || new Headers();
        options.method = method.toUpperCase();

        options.body = options.body || requestBody;
        if (searchParams) options.search = options.search || this.convertToUrlSearchParams(searchParams);

        options.headers.append('Accept', 'application/json');
       // options.headers.append('Access-Control-Allow-Origin', 'http://localhost:4200');
        //options.headers.append('Content-Type', 'application/json');

        if (Api.bearerToken && useBearerToken) {
            options.headers.append('Authorization', 'Bearer ' + Api.bearerToken);
        }
        console.log('endpoint:', this.env.settings.notifyApiUrl + relativeUrl);

        return this.http.request(this.env.settings.notifyApiUrl + relativeUrl, options)
            .map((res: Response) => { return res.json(); })
            .map((stream => { return stream; }))
            .share();;
    }

    private filePost<T>(relativeUrl: string, files: File[], useBearerToken = true): Observable<T> {
        return Observable.create(observer => {
            let formData: FormData = new FormData();

            let xhr: XMLHttpRequest = new XMLHttpRequest();

            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(xhr.response);
                    }
                }
            };

            for (let i = 0; i < files.length; i++) {
                formData.append('files[]', files[i], files[i].name)
            }

            xhr.open('POST', this.env.settings.apiUrl + relativeUrl, true);

            // xhr.setRequestHeader('Accept', 'application/json');
            if (Api.bearerToken && useBearerToken) {
                xhr.setRequestHeader('Authorization', 'Bearer ' + Api.bearerToken);
            }

            xhr.send(formData);
        });
    }
    public getBearerToken(username: string, password: string): Observable<Authorization> {
        let requestBody = new Token();
        let headers = new Headers();
        headers.append('Content-Type', 'application/x-www-form-urlencoded');

        requestBody.username = username;
        requestBody.password = password;
        requestBody.client_id = this.env.settings.apiClientId;
        requestBody.client_secret = this.env.settings.apiClientSecret;
        requestBody.grant_type = this.env.settings.apiGrantType;

        return this.request<Authorization>(HttpMethods.post, Endpoints.token.getUrl(), null, this.convertToUrlSearchParams(requestBody).toString(), headers, false);
    }



    public getAssessmentsAssignedToPatient(patientId: number): Observable<any> {
        let endpoint = Endpoints.getAssessmentsAssignedToPatient.getUrl({ 'patientId': patientId });
        return this.request<any>(HttpMethods.get, endpoint);
    }

    public getBHScreenSSO(intakeId: number): Observable<any> {
        let endpoint = Endpoints.getBHScreenSSO.getUrl({ 'intakeId': intakeId });

        return this.request<any>(HttpMethods.get, endpoint);
    }

    public getExternalUserId(userId: number): Observable<string> {
        let endpoint = Endpoints.getExternalUserId.getUrl({ 'userId': userId });

        return this.request<string>(HttpMethods.get, endpoint);
    }

    
    public getDependentsAllowed(userId: number): Observable<boolean> {
        let endpoint = Endpoints.getDependentsAllowed.getUrl({ 'userId': userId });
        return this.request<boolean>(HttpMethods.get, endpoint);
    }


    public emailExists(email: string): Observable<EmailExistsResult> {
        let emailExists = new EmailExists();
        let endpoint = Endpoints.emailExists.getUrl();

        emailExists.email = email;
        return this.request<EmailExistsResult>(HttpMethods.post, endpoint, null, emailExists);
    }
    public getMember(userId: number): Observable<Patient> {
        let endpoint = Endpoints.getPatient.getUrl({ 'userId': userId });

        return this.request<Patient>(HttpMethods.get, endpoint);
    }
    public forgotPassword(email: string): Observable<ForgotPasswordResult> {
        let forgotPassword = new ForgotPasswordResult();
        let endpoint = Endpoints.forgotPassword.getUrl();
        return this.request<ForgotPasswordResult>(HttpMethods.post, endpoint, null, { email: email });
    }
    public forgotPasswordES(email: string): Observable<ForgotPasswordResult> {
        let forgotPassword = new ForgotPasswordResult();
        let endpoint = Endpoints.forgotPasswordES.getUrl();
        return this.request<ForgotPasswordResult>(HttpMethods.post, endpoint, null, { email: email });
    }
    public setPassword(passwordReset: PasswordReset): Observable<string> {
        let endpoint = Endpoints.setPassword.getUrl();
        console.log('setPassword.getUrl', endpoint);
        return this.request<string>(HttpMethods.post, endpoint, null, passwordReset);
    }


    public getRegistrationStatus(email: string): Observable<PatientActivationInfo> {
        let emailExists = new EmailExists();
        let endpoint = Endpoints.getRegistrationStatus.getUrl();
        return this.request<PatientActivationInfo>(HttpMethods.get, endpoint , { email:   email  });
    }
    public getPatientPoliciesByLineOfBusiness(userId: number, lineOfBusiness: string): Observable<PatientPlan[]> {
        var endpoint = Endpoints.getPatientPoliciesByLineOfBusiness.getUrl({ 'userId': userId, 'lineOfBusiness': lineOfBusiness });
        let result = this.request<PatientPlan[]>(HttpMethods.get, endpoint);
        return result;
    }
    public capturePayment(intakeId: number): Observable<any> {
        let endpoint = Endpoints.capturePayment.getUrl({ 'intakeId': intakeId });
        return this.request<any>(HttpMethods.post, endpoint, null, null);
    }
    public completeIntake(intakeId: number, planId: number): Observable<WaitingRoom> {
        var endpoint = Endpoints.completeIntake.getUrl({ 'intakeId': intakeId, 'planId': planId });
        let result = this.request<WaitingRoom>(HttpMethods.post, endpoint, null, null);
        return result;
    }

    public processUserPaymentMethod(cardId, data): Observable<IntakeTransactionResponse> {
        let endpoint = Endpoints.processPayementforIntake.getUrl({ 'cardId': cardId });
        let res = this.request<IntakeTransactionResponse>(HttpMethods.post, endpoint, null, data);
        return res;
    }
    public scheduleUserPaymentMethod(cardId, data): Observable<any> {
        let endpoint = Endpoints.schedulePayementforIntake.getUrl({ 'cardId': cardId });
        let res = this.request<any>(HttpMethods.post, endpoint, null, data);
        return res;
    }

    public getUserProfile(userId: number): Observable<Profile> {
        let endpoint = Endpoints.getUserProfile.getUrl({ 'userId': userId });
        return this.request<Profile>(HttpMethods.get, endpoint);
    }

    //Patient
    public getPatientById(patientId: number): Observable<Patient> {
        let endpoint = Endpoints.getPatientById.getUrl({ 'patientId': patientId });
        return this.request<Patient>(HttpMethods.get, endpoint);
    }
    public getPatientPolicyType(userId: number): Observable<number> {
        let endpoint = Endpoints.getUserPolicyType.getUrl({ 'userId': userId });
        let result = this.request<number>(HttpMethods.get, endpoint);
        return result;
    }
    public getAvailableSchedules(url: string) {
        let result = this.request<any[]>(HttpMethods.get, url);
        return result;
    }
    public getPatientScheduledIntakes(patientId: number): Observable<PatientScheduledVisits[]> {
        let endpoint = Endpoints.getUpcomingVisits.getUrl({ 'patientId': patientId });
        let result = this.request<PatientScheduledVisits[]>(HttpMethods.get, endpoint);
        return result;
    }
    public getVisitSchedule(intakeId: number): Observable<VisitSchedule> {
        let endpoint = Endpoints.getBHVisitSchedule.getUrl({ 'intakeId': intakeId });
        return this.request<VisitSchedule>(HttpMethods.get, endpoint);
    }
    public updateScheduleRequestforIntake(intakeId: number, data): Observable<ScheduleResponse[]> {
        let endpoint = Endpoints.updateScheduleRequestforIntake.getUrl({ 'intakeId': intakeId });
        let res = this.request<ScheduleResponse[]>(HttpMethods.post, endpoint, null, data);
        return res;
    }
    public updatePatient(patientId: number, patient: Patient): Observable<Patient> {
        let endpoint = Endpoints.updatePatient.getUrl({ patientId: patientId });
        return this.request<Patient>(HttpMethods.put, endpoint, null, patient);
    }
    public updatePatientProfile(patientId: number, patient: Patient): Observable<Patient> {
        let endpoint = Endpoints.updatePatientProfile.getUrl({ patientId: patientId });
        return this.request<Patient>(HttpMethods.put, endpoint, null, patient);
    }
    public createPatient(patient: Patient): Observable<Patient> {
        let endpoint = Endpoints.createPatient.getUrl();
        return this.request<Patient>(HttpMethods.post, endpoint, null, patient);
    }
    public createPatientAvatar(userId: number, file: File): Observable<PatientAvatarResult> {
        var endpoint = Endpoints.createPatientAvatar.getUrl({ 'userId': userId });
        let patientAvatarResult = this.filePost<PatientAvatarResult>(endpoint, [file]);
        return patientAvatarResult;
    }
    public createIntake(intake: Intake): Observable<any> {
        var endpoint = Endpoints.createIntake.getUrl();
        let patientAvatarResult = this.request<any>(HttpMethods.post, endpoint, null, intake);
        return patientAvatarResult;
    }
    public getPreferredPharmacyList(userId: number): Observable<PharmacyInfo[]> {
        var endpoint = Endpoints.getPreferredPharmacy.getUrl({ 'userId': userId });
        let listofpharmacys = this.request<PharmacyInfo[]>(HttpMethods.get, endpoint);
        return listofpharmacys;
    }

    public getPatientHistoryAdditional(userId: number): Observable<PatientAdditional> {
        var endpoint = Endpoints.getPatientHistoryAdditional.getUrl({ 'userId': userId });
        let historyAdditional = this.request<PatientAdditional>(HttpMethods.get, endpoint);
        return historyAdditional;
    }
    public updatePatientSurvey(surveyData: SurveyModel): Observable<any> {
        var endpoint = Endpoints.upsertPatientSurvey.getUrl();
        return this.request<any>(HttpMethods.post, endpoint, null, surveyData);
    }
    public updatePatientAdditional(userId: number, patientAdditional: PatientAdditional): Observable<PatientAdditional> {
        let endpoint = Endpoints.updatesmokingalcohol.getUrl({ userId: userId });
        let updateResults = this.request<PatientAdditional>(HttpMethods.post, endpoint, null, patientAdditional);
        return updateResults;
    }
    public getPhycFollowUpStatus(patientId: number, providerId: number, intakeId: number): Observable<boolean> {
        let endpoint = Endpoints.checkPhycFollowUpStatus.getUrl({ 'patientId': patientId, 'providerId': providerId, 'intakeId': intakeId });
        let result = this.request<boolean>(HttpMethods.get, endpoint);
        return result;
    }


    //Intake
    public getActiveIntake(userId: number): Observable<Intake> {
        let endpoint = Endpoints.getActiveIntake.getUrl({ userId: userId });
        let result = this.request<Intake>(HttpMethods.get, endpoint);
        return result;
    }
    public getIntake(intakeId: number): Observable<Intake> {
        var endpoint = Endpoints.getIntake.getUrl({ 'intakeId': intakeId });
        let result = this.request<Intake>(HttpMethods.get, endpoint);
        return result;
    }
    public getIntakePDF(intakeId: number): Observable<any> {
        let endpoint = Endpoints.getIntakePDF.getUrl({ 'intakeId': intakeId });
        let result = this.request<any>(HttpMethods.get, endpoint);
        return result;
    }
    public getExcuseForm(intakeId: number): Observable<any> {
        let endpoint = Endpoints.getExcuseForm.getUrl({ 'intakeId': intakeId });
        let result = this.request<any>(HttpMethods.get, endpoint);
        return result;
    }
    public getIntakeMedications(intakeId: number): Observable<IntakeMedication[]> {
        let endpoint = Endpoints.getIntakeMedications.getUrl({ 'intakeId': intakeId });
        return this.request<IntakeMedication[]>(HttpMethods.get, endpoint);
    }
    public insertIntakeMedications(intakeId: number, data: string[]): Observable<IntakeMedication[]> {
        let endpoint = Endpoints.insertIntakeMedications.getUrl({ 'intakeId': intakeId });
        return this.request<IntakeMedication[]>(HttpMethods.post, endpoint, null, data);
    }
    public getIntakeAllergens(intakeId: number): Observable<IntakeAllergen[]> {
        let endpoint = Endpoints.getIntakeAllergens.getUrl({ 'intakeId': intakeId });
        return this.request<IntakeAllergen[]>(HttpMethods.get, endpoint);
    }
    public insertIntakeAllergens(intakeId: number, data: string[]): Observable<IntakeAllergen[]> {
        let endpoint = Endpoints.insertIntakeAllergens.getUrl({ 'intakeId': intakeId });
        return this.request<IntakeAllergen[]>(HttpMethods.post, endpoint, null, data);
    }

    //Dependents
    public getDependents(userId: number): Observable<Patient[]> {
        let endpoint = Endpoints.getDependents.getUrl({ userId: userId });
        return this.request<Patient[]>(HttpMethods.get, endpoint);
    }
    public createDependent(userId: number, dependent: Patient): Observable<Patient> {
        let endpoint = Endpoints.createDependent.getUrl({ userId: userId });
        return this.request<Patient>(HttpMethods.post, endpoint, null, dependent);
    }
    public createDependentES(userId: number, dependent: Patient): Observable<Patient> {
        let endpoint = Endpoints.createDependentES.getUrl({ userId: userId });
        return this.request<Patient>(HttpMethods.post, endpoint, null, dependent);
    }
    public updateDependent(userId: number, dependent: Patient): Observable<Patient> {
        let endpoint = Endpoints.updateDependent.getUrl({ userId: userId });
        return this.request<Patient>(HttpMethods.put, endpoint, null, dependent);
    }

    //Search
    public searchPharmacy(distance: number, nPage: number, data): Observable<PatientPreferedPharmacy[]> {
        let endpoint = Endpoints.searchPharmacy.getUrl({ 'distance': distance, 'nPage': nPage });
        let res = this.request<PatientPreferedPharmacy[]>(HttpMethods.post, endpoint, null, data);
        return res;
    }
    public updateIntakePharmacy(intakeId: number, pharmacyId: number): Observable<any> {
        var endpoint = Endpoints.updateIntakePharmacy.getUrl({ 'intakeId': intakeId });
        let result = this.request<any>(HttpMethods.post, endpoint, null, { pharmacyId });
        return result;
    }

    //Provider
    public getProviderBio(lineOfBusiness, stateCode): Observable<ProviderBio[]> {
        let endpoint = Endpoints.getProviderBios.getUrl({ 'lineOfBusiness': lineOfBusiness, 'stateCode': stateCode });

        return this.request<ProviderBio[]>(HttpMethods.get, endpoint);
    }
    public getFullProviderBio(providerId): Observable<ProviderBioFullDetail> {
        let endpoint = Endpoints.getProviderBioDetail.getUrl({ 'providerId': providerId });

        return this.request<ProviderBioFullDetail>(HttpMethods.get, endpoint);
    }

    public getProviderBioByBhType(lineOfBusiness, stateCode, bhType): Observable<ProviderBio[]> {
        let endpoint = Endpoints.getProviderBiosByType.getUrl({ 'lineOfBusiness': lineOfBusiness, 'stateCode': stateCode, 'bhType': bhType });

        return this.request<ProviderBio[]>(HttpMethods.get, endpoint);
    }

    //Get Intake Doc
    public getIntakeFile(intakeId: number): Observable<Array<ChartFile>> {
        let endpoint = Endpoints.getIntakeFile.getUrl({ 'intakeId': intakeId });
        return this.request<Array<ChartFile>>(HttpMethods.get, endpoint);
    }
    public uploadIntakeFiles(intakeId: number, files: File[]): Observable<any> {
        let endpoint = Endpoints.uploadIntakeFiles.getUrl({ 'intakeId': intakeId });
        let fileresult = this.filePost<any>(endpoint, files);
        return fileresult;
    }
    public removeIntakeFile(intakeId: number, fileId: number): Observable<any> {
        let endpoint = Endpoints.removeIntakeFile.getUrl({ 'intakeId': intakeId, 'fileId': fileId });
        return this.request<any>(HttpMethods.get, endpoint);
    }

    //Visit History
    public getExamSummary(userId: number): Observable<ExamSummary[]> {
        let endpoint = Endpoints.getExamSummary.getUrl({ userId: userId });
        return this.request<ExamSummary[]>(HttpMethods.get, endpoint);
    }
    public getExamDetail(intakeId: number): Observable<ExamData> {
        let endpoint = Endpoints.getExamDetail.getUrl({ intakeId: intakeId });
        return this.request<ExamData>(HttpMethods.get, endpoint);
    }
    public getCareInstructionsPdfLinks(intakeId: number): Observable<string[]> {
        let endpoint = Endpoints.getCareInstructionsPdfLinks.getUrl({ intakeId: intakeId });
        let careDocs = this.request<string[]>(HttpMethods.get, endpoint);
        return careDocs;
    }

    //Calendar//    
    public getStates(): Observable<State[]> {
        let endpoint = Endpoints.getStates.getUrl();
        return this.request<State[]>(HttpMethods.get, endpoint);
    }
    public getTimezones(): Observable<Timezone[]> {
        let endpoint = Endpoints.getTimezones.getUrl();
        return this.request<Timezone[]>(HttpMethods.get, endpoint);
    }
    public checkStateIsValidForService(stateCodeCheck: StateCodeCheck): Observable<any> {
        var endpoint = Endpoints.checkStateIsValidForService.getUrl();
        let result = this.request<any>(HttpMethods.post, endpoint, null, stateCodeCheck);
        return result;
    }


    //Medical Conditions
    public updatePatientMedicalConditions(userId: number, conditionData: MedicalCondition): Observable<PatientMedicalCondition[]> {
        let endpoint = Endpoints.updateMedicalConditions.getUrl({ userId: userId });
        let updateResults = this.request<PatientMedicalCondition[]>(HttpMethods.post, endpoint, null, conditionData);
        return updateResults;
    }
    public insertPatientMedicalConditions(userId: number, conditionData: MedicalCondition): Observable<PatientMedicalCondition[]> {
        let endpoint = Endpoints.insertMedicalConditions.getUrl({ userId: userId });
        let updateResults = this.request<PatientMedicalCondition[]>(HttpMethods.post, endpoint, null, conditionData);
        return updateResults;
    }
    public deletePatientMedicalConditions(userId: number, conditionId: number): Observable<PatientMedicalCondition[]> {
        let endpoint = Endpoints.deletePatientCondition.getUrl({ userId: userId, conditionId: conditionId });
        let deleteResults = this.request<PatientMedicalCondition[]>(HttpMethods.delete, endpoint);
        return deleteResults;
    }
    public searchMedicalConditions(description: string): Observable<PatientMedicalCondition[]> {
        var endpoint = Endpoints.getMedicalConditions.getUrl({ 'description': description });
        let listofconditions = this.request<PatientMedicalCondition[]>(HttpMethods.get, endpoint);
        return listofconditions;
    }
    public getSearchMedicalConditionsByLob(description: string, lob: string): Observable<MedicalCondition[]> {
        var endpoint = Endpoints.getMedicalConditionsByLob.getUrl({ 'description': description, 'lob': lob });
        let listofconditions = this.request<MedicalCondition[]>(HttpMethods.get, endpoint);
        return listofconditions;
    }
    public getConditionsByServiceLine(lob: string): Observable<ConditionDisplay[]> {
        var endpoint = Endpoints.getMedicalConditionsByServiceLine.getUrl({ 'lob': lob });
        let listofconditions = this.request<ConditionDisplay[]>(HttpMethods.get, endpoint);
        return listofconditions;
    }
    public getPatientSurgeries(userId: number): Observable<Surgery[]> {
        var endpoint = Endpoints.getPatientSurgeries.getUrl({ 'userId': userId });
        let listofconditions = this.request<Surgery[]>(HttpMethods.get, endpoint);
        return listofconditions;
    }
    public updatePatientSurgeries(userId: number, surgeries: Surgery[]): Observable<Surgery[]> {
        let endpoint = Endpoints.updatePatientSurgeries.getUrl({ userId: userId });
        let updateResults = this.request<Surgery[]>(HttpMethods.post, endpoint, null, surgeries);
        return updateResults;
    }
    public insertPatientSurgery(userId: number, surgery: Surgery): Observable<Surgery[]> {
        let endpoint = Endpoints.insertPatientSurgery.getUrl({ userId: userId });
        let updateResults = this.request<Surgery[]>(HttpMethods.put, endpoint, null, surgery);
        return updateResults;
    }
    public deletePatientSurgery(userId: number, surgeryId: number): Observable<boolean> {
        let endpoint = Endpoints.deletePatientSurgery.getUrl({ userId: userId, surgeryId: surgeryId });
        let deleteResults = this.request<boolean>(HttpMethods.post, endpoint);
        return deleteResults;
    }
    public searchSurgeries(description: string): Observable<Surgery[]> {
        var endpoint = Endpoints.searchSurgeries.getUrl({ 'description': description });
        let listofconditions = this.request<Surgery[]>(HttpMethods.get, endpoint);
        return listofconditions;
    }
    public getSurgicalHistoryListItems(): Observable<SurgicalList[]> {
        var endpoint = Endpoints.getSurgicalList.getUrl();
        let listofsurgery = this.request<SurgicalList[]>(HttpMethods.get, endpoint);
        return listofsurgery;
    }

    public getPatientAllergens(userId: number): Observable<Allergen[]> {
        var endpoint = Endpoints.getPatientAllergenList.getUrl({ 'userId': userId });
        let listofallergens = this.request<Allergen[]>(HttpMethods.get, endpoint);
        return listofallergens;
    }
    public updatePatientAllergy(userId: number, allergenData: PatientAllergen): Observable<AllergenData> {
        let endpoint = Endpoints.updatePatientAllergy.getUrl({ userId: userId });
        let updateResults = this.request<AllergenData>(HttpMethods.post, endpoint, null, allergenData);
        return updateResults;
    }
    public deletePatientAllergy(userId: number, allergyId: number): Observable<boolean> {
        let endpoint = Endpoints.deletePatientAllergy.getUrl({ userId: userId, allergyId: allergyId });
        let deleteResults = this.request<boolean>(HttpMethods.delete, endpoint);
        return deleteResults;
    }
    public searchAllergens(description: string): Observable<Allergen[]> {
        var endpoint = Endpoints.getAllergens.getUrl({ 'description': description });
        let listofallergens = this.request<Allergen[]>(HttpMethods.get, endpoint);
        return listofallergens;
    }
    public searchMedication(searchName: string): Observable<string[]> {
        let endpoint = Endpoints.searchMedication.getUrl({ 'searchName': searchName });
        let results = this.request<string[]>(HttpMethods.get, endpoint);
        return results;
    }
    public insertMediaSettingData(mediaData: MediaPermisionData): Observable<any> {
        let endpoint = Endpoints.saveMediaConfig.getUrl();
        return this.request<any>(HttpMethods.post, endpoint, null, mediaData);
    }



    public getPatientPlans(patientId: number, lineOfBusiness: string): Observable<any> {
        let endpoint = Endpoints.getPatientPlans.getUrl({ 'patientId': patientId, 'lineOfBusiness': lineOfBusiness });
        let result = this.request<any>(HttpMethods.get, endpoint);
        return result;
    }

    public getAllPatientPlans(patientId: number): Observable<any> {
        let endpoint = Endpoints.getAllPatientPlans.getUrl({ 'patientId': patientId });
        let result = this.request<any>(HttpMethods.get, endpoint);
        return result;
        //return this._api.awsRequest<any>(HttpMethods.get, `patients/${patientId}/plans`);
    }

    public getPatientPlanTypes(userId: number): Observable<PatientPlanTypes[]> {
        let endpoint = Endpoints.getPatientPlanTypes.getUrl({ 'userId': userId });

        return this.request<PatientPlanTypes[]>(HttpMethods.get, endpoint);
    }

    public getProviderAvailabilityQueue(data: ProviderScheduleSearch): Observable<AvailableProviderQueue[]> {
        let endpoint = Endpoints.getProviderAvailabilityQueue.getUrl();
        return this.request<AvailableProviderQueue[]>(HttpMethods.post, endpoint, null, data);
    }
    public getProviderScheduleForPatientChange(scheduleId): Observable<ProviderScheduleForPatient> {
        let endpoint = Endpoints.getProviderScheduleForPatientChange.getUrl({ 'scheduleId': scheduleId });
        return this.request<ProviderScheduleForPatient>(HttpMethods.get, endpoint);
    }
    public saveScheduledVisitForPatient(data, lob, providerId): Observable<any> {
        let endpoint = Endpoints.saveScheduleForPatient.getUrl({ 'providerId': providerId, 'lob': lob });
        return this.request<any>(HttpMethods.post, endpoint, null, data);
    }
    public saveScheduledVisitWithProvider(intakeId, data): Observable<any> {
        let endpoint = Endpoints.saveScheduleRequestWithProvider.getUrl({ 'intakeId': intakeId });
        return this.request<any>(HttpMethods.post, endpoint, null, data);
    }
    public saveScheduleRequest(intakeId: number, data): Observable<ScheduleResponse[]> {
        let endpoint = Endpoints.updateScheduleRequestforIntake.getUrl({ 'intakeId': intakeId });
        let res = this.request<ScheduleResponse[]>(HttpMethods.post, endpoint, null, data);
        return res;
    }
    public saveScheduleIntakeAppointment(data): Observable<PatientScheduleIntakeData[]> {
        let endpoint = Endpoints.saveScheduledIntakeForPatient.getUrl();
        let res = this.request<PatientScheduleIntakeData[]>(HttpMethods.post, endpoint, null, data);
        return res;
    }
    public makeScheduleRequest(data): Observable<any> {
        let endpoint = Endpoints.makeScheduleRequest.getUrl();
        let res = this.request<any>(HttpMethods.post, endpoint, null, data);
        return res;
    }
    public confirmScheduleRequest(requestId): Observable<any> {
        let endpoint = Endpoints.confirmcheduleRequest.getUrl({'requestId': requestId});
        let res = this.request<any>(HttpMethods.post, endpoint, null, null);
        return res;
    }
    //  static confirmcheduleRequest = new Endpoint('v2/schedule/patientAppointment/confirm/{requestId}');
    //Twilio//
    // public getAllProviders(): Observable<User[]> {
    //     let endpoint = Endpoints.getAllProviders.getUrl();
    //     return this.request<User[]>(HttpMethods.get, endpoint);
    // }
    // public sendProviderSMS(providerId: number, phoneNumber: string, message: string): Observable<any> {
    //     let data = { 'PhoneNumber' : phoneNumber, 'Message': message};
    //     let endpoint = Endpoints.getProviderSMS.getUrl({'providerId': providerId});
    //     return this.request<any>(HttpMethods.post, endpoint, null, data);
    // }
    // public getProviderNewReply(): Observable<RetrySMS[]> {
    //     let endpoint = Endpoints.getProviderNewReply.getUrl();
    //     return this.request<RetrySMS[]>(HttpMethods.get, endpoint);
    // }

    public saveUserPaymentMethod(userId, data): Observable<PaymentMethodResult> {
        let endpoint = Endpoints.saveNewPaymentMethod.getUrl({ 'userId': userId });
        let res = this.request<PaymentMethodResult>(HttpMethods.post, endpoint, null, data);
        return res;
    }
    public getuserPaymentMethods(userId): Observable<PaymentMethodResult[]> {
        let endpoint = Endpoints.getPaymentMethods.getUrl({ 'userId': userId });
        let res = this.request<PaymentMethodResult[]>(HttpMethods.get, endpoint);
        return res;
    }
    public updateUserPaymentMethods(cardId, data): Observable<PaymentMethodResult> {
        let endpoint = Endpoints.updatePaymentMethod.getUrl({ 'cardId': cardId });
        let res = this.request<PaymentMethodResult>(HttpMethods.put, endpoint, null, data);
        return res;
    }

    public updatePatienthasLabwork(intakeId): Observable<LabworkRequestModel> {
        let endpoint = Endpoints.updatePatienthasLabwork.getUrl({ 'intakeId': intakeId });
        let res = this.request<LabworkRequestModel>(HttpMethods.post, endpoint, null, null);
        return res;
    }

    public getLabworkRequestsByUserId(userId, status): Observable<LabworkRequestModel[]> {
        let endpoint = Endpoints.getPatientLabwork.getUrl({ 'userId': userId });
        endpoint = endpoint + '/' + status;

        let res = this.request<LabworkRequestModel[]>(HttpMethods.get, endpoint);
        return res;
    }

    //Notify APIS
    public startReserveSchedule(scheduleData): Observable<any> {
        let endpoint = Endpoints.reserveScheduleRequest.getUrl();
        let res = this.notify<any>(HttpMethods.post, endpoint, null, scheduleData);
        //let res = this.notify<any[]>(HttpMethods.get, endpoint);
        return res;
    }
    public cancelcheduledReservation(reservationId): Observable<any> {
        let endpoint = Endpoints.cancelReservedScheduleRequest.getUrl({'jobId': reservationId});
        let res = this.notify<any>(HttpMethods.post, endpoint, null, null);
        //let res = this.notify<any[]>(HttpMethods.get, endpoint);
        return res;
    }
    public verifyScheduledReservation(requestId): Observable<RequestVerification> {
        let endpoint = Endpoints.verifyReservedScheduleRequest.getUrl({'requestId': requestId});
        let res = this.request<RequestVerification>(HttpMethods.get, endpoint);
        return res;
    }


 

}
